import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal } from "@angular/core";
import { FlexDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { LoggingService, NavigationService, UrlService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { AppLinkParms, ExecutionResult, FilterItem, IChartCriteria, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "purchasing-requisition-status-chart",
    templateUrl: "./requisitionStatusChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        FlexDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class RequisitionStatusChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartService = inject(ChartService);
    private readonly $urlService = inject(UrlService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $intentService = inject(IntentService);

    private readonly destructor$ = new Subject<void>();
    private readonly _chartId: string;

    readonly showChart = signal(false);
    readonly department = signal<string>("");
    chartConfig: ChartConfiguration<"bar">;

    private _chartCriteria: IChartCriteria;

    ChartTitle: string = "Open Requisition Status";
    ChartSubtitle: string;
    isLoaded: boolean;
    total: string;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get ariaLabel(): string { return `Card-${this.ChartTitle}Chart-Purchasing`; }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }


    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    chartClass(): string
    {
        return this.chartId.replace(".", "-")
            .toLowerCase() + "-class";
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.some(x => x.Data?.length > 0);
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;

        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.department.set(this.buildDepartmentList(result));

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private updateChartConfiguration(): void
    {
        this.total = "$0.00";
        const amount = this.chartCriteria.ChartDatas[0].Data.reduce((a, value) => a + value.Total, 0);
        this.total = "$" + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        this.chartConfig = {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                const label = tooltipItem.label;
                                const count = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Count;
                                const value = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Total;
                                return `${label} ( Count: ${count}; Total: ${formatCurrency(value, this.locale, "$")} )`;
                            },
                        },
                    },
                },
                scales: {
                    y: {
                        type: "linear",
                        display: true,
                        grid: { display: true },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(
                                    value > -10 && value < 10
                                        ? value.toFixed(2)
                                        : value.toFixed(0));
                            },
                        },
                    }
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element) return;
                    const index = element.index;
                    const filter: FilterItem[] = [
                        {
                            Key: "Approval Status",
                            Value: this.chartCriteria.ChartDatas[0].Labels[index],
                        },
                        {
                            Key: "Department",
                            Value: this.department(),
                        }
                    ];

                    if (this.chartCriteria.ChartDatas[0].Labels[index] === "Waiting for Approval")
                    {
                        filter.push({
                            Key: "Approvable by Me",
                            Value: "true",
                        });
                    }

                    const app = "requisitions";
                    const parms: AppLinkParms = {
                        App: app,
                        Parms: filter,
                    };
                    this.$urlService.GetAppLink(parms)
                        .then(({ Result: result }: ExecutionResult<string>) =>
                        {
                            const index = result.indexOf("?");
                            if (result.includes("apps/form"))
                            {
                                this.$navigationService.navigateToApp(result);
                            }
                            else if (index > 0)
                            {
                                this.$intentService.resolve("Requisitions", "Search", {
                                    querystring: result.substring(index, result.length),
                                });
                            }
                        });
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [{
                    type: "bar",
                    data: this.chartCriteria.ChartDatas[0].Data.map(x => x.Total),
                    backgroundColor: this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data.length),
                }],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    private buildDepartmentList(chartCriteria: IChartCriteria): string
    {
        const departmentParam = chartCriteria?.Parameters?.find(x => x.Id === "RequisitionStatusDepartment");
        const departments = departmentParam?.Value || null;
        if (!departments)
        {
            return "";
        }

        let departmentNames = "";
        const departmentIds = departments.split(",");
        const options = departmentParam?.Options;
        for (const opt of options)
        {
            if (departmentIds.indexOf(opt.Value) > -1)
            {
                departmentNames = departmentNames + opt.Display.trim() + ", ";
            }
        }
        return departmentNames.trim()
            .slice(0, -1);
    }
}
